import { CONDITIONS, Cats, SubCats } from '../../generated/static-categories';
import { parseTagFacets } from '../../utils/tag-facet-param';

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** A list param as the search context or the URL holds it: an array or a comma string. */
function list(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  return raw.map(v => String(v).trim()).filter(Boolean);
}

/** Kids & Baby, where every listing is a child's. */
const KIDS_GROUP_ID = 20;

const GENDER: Record<string, string> = { female: "Women's", male: "Men's", unisex: 'Unisex' };
const CHILD_GENDER: Record<string, string> = { female: "Girls'", male: "Boys'", unisex: 'Unisex' };
const AGE: Record<string, string> = { adult: 'Adult', kids: "Kids'", baby: 'Baby' };

/**
 * Who the items are for: "Women's", "Boys'", "Baby". Girls are stored female
 * and boys male, so where every listing is a child's the gender reads as
 * Girls'/Boys'. A gender already says the age, so the age shows only alone.
 */
function audience(params: Record<string, unknown>): string | null {
  const ages = list(params.ageGroup);
  const children = (ages.length > 0 && ages.every(a => a === 'kids' || a === 'baby'))
    || Number(params.categoryGroupId) === KIDS_GROUP_ID;
  const g = list(params.gender).map(v => (children ? CHILD_GENDER : GENDER)[v]).filter(Boolean);
  const words = g.length ? g : ages.map(v => AGE[v]).filter(Boolean);
  return words.length ? words.join(' / ') : null;
}

/**
 * Build a human-readable description of active search params.
 * e.g. "Brand New Women's Black Nike shoes size 8 under $100 in Fashion with shipping"
 */
export function humanizeSearchParams(params: Record<string, unknown>): string | null {
  const parts: string[] = [];
  const facets = parseTagFacets(params.tf);

  // Condition (e.g. "Brand New")
  if (params.itemCondition) {
    const condIds = Array.isArray(params.itemCondition) ? params.itemCondition : [params.itemCondition];
    const labels = condIds.map((id: number) => CONDITIONS.find(c => c.id === id)?.label).filter(Boolean);
    if (labels.length) parts.push(labels.join(' / '));
  }

  // Who it is for, then what it looks like and who made it
  const who = audience(params);
  if (who) parts.push(who);
  for (const key of ['color', 'brand', 'material'] as const) {
    const values = list(params[key]).map(capitalize);
    if (values.length) parts.push(values.join(' / '));
  }

  // Tag filters (e.g. "Dogs / Cats"), in the order the URL holds them
  for (const [key, values] of Object.entries(facets ?? {})) {
    if (key !== 'type') parts.push(values.map(capitalize).join(' / '));
  }

  // Search term
  if (params.q) {
    parts.push(params.q as string);
  }

  // Item type (category ids)
  const types = (facets?.type ?? []).map(id => SubCats[Number(id)]?.label).filter(Boolean);
  if (types.length) parts.push(types.join(' / '));

  // Size
  const sizes = list(params.size);
  if (sizes.length) parts.push(`size ${sizes.join(' / ')}`);

  // Price range
  const minP = params.minPrice || params.min;
  const maxP = params.maxPrice || params.max;
  if (minP && maxP) {
    parts.push(`$${minP} - $${maxP}`);
  } else if (maxP) {
    parts.push(`under $${maxP}`);
  } else if (minP) {
    parts.push(`over $${minP}`);
  }

  // Category
  if (params.categoryGroupId) {
    const cat = Cats[params.categoryGroupId as keyof typeof Cats];
    if (cat?.label) parts.push(`in ${cat.label}`);
  }

  // Modifiers
  if (params.shippable) parts.push('with shipping');
  if (params.sold) parts.push('sold');
  if (params.deals) parts.push('deals');
  if (params.quality) parts.push('top quality');

  return parts.length > 0 ? parts.join(' ') : null;
}
